import React, { useEffect, useState } from 'react';
import { Button } from 'components/ui/button';
import { Icon } from 'components/blocks/menu-icon/menu-icon';
import { NotificationSideSheet } from './NotificationSideSheet';

import { useNotificationFeedStore } from 'state/store/notifications/use-notification-feed-store';
import { useNotificationStore } from 'state/store/common/useNotificationStore';

import { useTheme } from 'components/core/theme-provider';
import { useToast } from 'hooks/use-toast';
import { useTranslation } from 'react-i18next';
import { cn } from 'lib/utils';
import { TooltipContent, Tooltip, TooltipTrigger, TooltipProvider } from 'components/ui/tooltip';
import { useCommonStore } from 'state/store/common/useCommonStore';
import { isAgentShareFromCurrentUser } from '../utils/agent-share-notification';
// import { useLocation } from 'react-router-dom';

export const NotificationBell: React.FC = () => {
    // const location = useLocation();
    const { t } = useTranslation();
    const { theme } = useTheme();
    const { toast } = useToast();
    const [isOpen, setIsOpen] = useState(false);
    const notification = useNotificationStore((state) => state.notification);
    const clearNotification = useNotificationStore((state) => state.clearNotification);
    const currentUserId = useCommonStore((state) => state.user?.id);

    const { fetchAndSetNotification, unseenCount, setUnseenCount, page } = useNotificationFeedStore();

    useEffect(() => {
        if (isOpen) {
            fetchAndSetNotification(page);
        }
    }, [isOpen, page]);

    useEffect(() => {
        if (!notification?.message?.denormalizedPayload) return;

        const rawPayload = notification.message.denormalizedPayload;

        try {
            const payload = typeof rawPayload === 'string' ? JSON.parse(rawPayload) : rawPayload;

            // 1. Custom / Legacy payload structure (payload.Message)
            if (payload?.Message && payload.Message.type !== 'file_upload') {
                if (isAgentShareFromCurrentUser(payload, currentUserId)) {
                    clearNotification();
                    return;
                }

                const verb = payload.Message.Verb || payload.Message.verb;
                const title = payload.Message.title || 'Notification';
                const description = payload.Message.description;
                const organizationName = payload.Message.displayName;

                setUnseenCount(unseenCount + 1);
                if (payload.Message.type === 'COLLABORATION') {
                    return;
                }

                if (verb === 'REMOVE' || verb === 'FAILED' || verb === 'CANCEL') {
                    toast({
                        title: t(title) || title,
                        description: t(description, { organizationName: `${organizationName}` }) || description,
                        variant: 'destructive',
                        duration: 3000,
                    });
                } else if (verb === 'ADD' || verb === 'ACCEPT' || verb === 'CHANGE' || verb === 'SHARE') {
                    toast({
                        title: title,
                        description: t(description, { organizationName: `${organizationName}` }) || description,
                        variant: 'success',
                        duration: 3000,
                    });
                }
                clearNotification();
                return;
            }

            // 2. Standard Blocks-OS payload structure (title, description, toastable, isSuccess)
            if (payload?.title || payload?.description) {
                const title = payload.title || 'Notification';
                const description = payload.description || '';
                const isFailure = payload.isSuccess === false || payload.status === 'FAILED';
                const shouldToast = payload.toastable !== false;

                setUnseenCount(unseenCount + 1);

                if (shouldToast && (title || description)) {
                    toast({
                        title: t(title) || title,
                        description: t(description) || description,
                        variant: isFailure ? 'destructive' : 'success',
                        duration: 3000,
                    });
                }
                clearNotification();
                return;
            }

        } catch (err) {
            // eslint-disable-next-line no-console
            console.error('Error parsing notification payload:', err);
            // Clear notification even on error to prevent retry loops
            clearNotification();
        }
    }, [notification]);

    const handleClick = () => {
        setIsOpen(true);
    };


    return (
        <>
            <Button
                variant="ghost"
                size="icon"
                onClick={handleClick}
                aria-label="View Notifications"
                className={cn(
                    'relative flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center self-center rounded-full p-1.5 text-primary transition-opacity hover:bg-transparent hover:opacity-80',
                    theme === 'dark' ? 'border border-border bg-[#0000]' : 'border border-[#d1d1d1] bg-white'
                )}
            >
                <TooltipProvider>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <div>
                                <Icon
                                    name="Bell"
                                    size={20}
                                    strokeWidth={2}
                                    className={theme === 'dark' ? 'text-[#ffffff]' : 'text-[#6D6D6D]'}
                                />
                            </div>
                        </TooltipTrigger>
                        <TooltipContent>
                            <p>{t('NOTIFICATIONS') || 'Notifications'}</p>
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>


                {
                    unseenCount > 0 && (
                        <div className="absolute w-[18px] h-[18px] -top-2 -right-1 flex items-center justify-center rounded-full bg-primary text-destructive-foreground text-[10px] font-semibold z-10">
                            {unseenCount > 99 ? '99+' : unseenCount > 0 ? unseenCount : 0}
                        </div>
                    )
                }


            </Button>
            <NotificationSideSheet isOpen={isOpen} onClose={() => setIsOpen(false)} />
        </>
    );
};

